import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import { CheckCircle2 } from 'lucide-react';
import { ReactComponent as WindowsLogo } from '../assets/WindowsLogo.svg';
import api from '../services/api';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';

const WindowsLogoIcon = (props) => (
  <SvgIcon component={WindowsLogo} inheritViewBox {...props} />
);

const getPlatformName = (platform) => {
  switch (platform) {
    case 'macos':
      return 'macOS';
    case 'ios':
      return 'iOS';
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
};

const getPlatformDescription = (platform) => {
  switch (platform) {
    case 'windows':
      return 'For Windows 10 and 11';
    case 'macos':
      return 'For Apple Silicon Macs';
    case 'ios':
      return 'For iPhone and iPad';
    case 'android':
      return 'For Android phones and tablets';
    default:
      return '';
  }
};

const getPlatformIcon = (platform, available) => {
  const iconColor = available ? publicPalette.heroBlueDeep : alpha(publicPalette.inkSoft, 0.6);
  const iconProps = { sx: { fontSize: 52, color: iconColor } };

  switch (platform) {
    case 'windows':
      return <WindowsLogoIcon {...iconProps} />;
    case 'macos':
      return <DesktopMacIcon {...iconProps} />;
    case 'ios':
      return <AppleIcon {...iconProps} />;
    case 'android':
      return <AndroidIcon {...iconProps} />;
    default:
      return <WindowsLogoIcon {...iconProps} />;
  }
};

const getDefaultPlatformData = () => ['windows', 'macos', 'ios', 'android'].map((platform) => ({
  id: platform,
  name: getPlatformName(platform),
  icon: getPlatformIcon(platform, false),
  description: getPlatformDescription(platform),
  filename: 'Coming soon',
  version: 'N/A',
  externalDownloadUrl: '',
  available: false,
  buildId: null,
}));

function DownloadsPage() {
  const [platformDownloads, setPlatformDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadError, setDownloadError] = useState('');
  const [activeDownloadId, setActiveDownloadId] = useState('');

  useEffect(() => {
    let isActive = true;

    const fetchBuilds = async () => {
      try {
        const response = await api.get('/admin/builds/active');
        const builds = response.data || [];
        const platforms = ['windows', 'macos', 'ios', 'android'];
        const platformData = platforms.map((platform) => {
          const build = builds.find((candidate) => candidate.platform === platform);

          if (!build) {
            return {
              id: platform,
              name: getPlatformName(platform),
              icon: getPlatformIcon(platform, false),
              description: getPlatformDescription(platform),
              filename: 'Coming soon',
              version: 'N/A',
              externalDownloadUrl: '',
              available: false,
              buildId: null,
            };
          }

          return {
            id: build._id || platform,
            name: getPlatformName(platform),
            icon: getPlatformIcon(platform, true),
            description: getPlatformDescription(platform),
            filename: build.originalFilename || 'Download available',
            version: build.version || 'Latest',
            externalDownloadUrl: build.externalDownloadUrl || '',
            available: true,
            buildId: build._id,
          };
        });

        if (isActive) {
          setPlatformDownloads(platformData);
        }
      } catch (error) {
        console.error('Failed to fetch builds:', error);
        if (isActive) {
          setPlatformDownloads(getDefaultPlatformData());
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchBuilds();

    return () => {
      isActive = false;
    };
  }, []);

  const recordDownloadClick = async (buildId) => {
    if (!buildId) {
      return;
    }

    try {
      await api.post(`/admin/builds/${buildId}/download-click`);
    } catch (error) {
      console.warn('Unable to record download click:', error);
    }
  };

  const handleDownload = async (platform) => {
    setDownloadError('');

    if (platform.externalDownloadUrl) {
      await recordDownloadClick(platform.buildId);
      window.open(platform.externalDownloadUrl, '_blank', 'noopener');
      return;
    }

    if (!platform.available || !platform.buildId) {
      setDownloadError('That platform is not available for download yet.');
      return;
    }

    setActiveDownloadId(platform.id);
    try {
      const response = await api.get(`/admin/builds/${platform.buildId}/download-url`);
      const { downloadUrl } = response.data;
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError('Download failed. Please try again later.');
    } finally {
      setActiveDownloadId('');
    }
  };

  return (
    <PublicPageFrame containerMaxWidth="xl" contentMaxWidth={1180}>
      <PublicCardHeader
        eyebrow="Downloads"
        title="Choose your platform"
        description="Download the latest available Virtual Medic build for your device."
      />

      <Stack spacing={3}>
        {downloadError && <Alert severity="error">{downloadError}</Alert>}

        {loading ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
              Loading available builds...
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {platformDownloads.map((platform) => (
              <Grid item xs={12} sm={6} key={platform.id}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 2,
                    p: 2.5,
                    borderRadius: '24px',
                    bgcolor: platform.available ? '#ffffff' : alpha(publicPalette.heroBlueCloud, 0.72),
                    border: `1px solid ${alpha(platform.available ? publicPalette.heroBlueMid : publicPalette.inkSoft, platform.available ? 0.14 : 0.1)}`,
                    boxShadow: platform.available ? '0 18px 40px rgba(18, 61, 103, 0.06)' : 'none',
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '20px',
                        bgcolor: alpha(publicPalette.heroBlue, platform.available ? 0.08 : 0.04),
                      }}
                    >
                      {platform.icon}
                    </Box>
                    <Stack spacing={0.6}>
                      <Typography
                        sx={{
                          color: publicPalette.ink,
                          fontWeight: 800,
                          fontSize: '1.15rem',
                          fontFamily: publicHeadingFontFamily,
                        }}
                      >
                        {platform.name}
                      </Typography>
                      <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                        {platform.description}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.4}>
                      <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, wordBreak: 'break-word' }}>
                        {platform.filename}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircle2 size={16} color={platform.available ? publicPalette.heroBlueMid : alpha(publicPalette.inkSoft, 0.7)} />
                      <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                        {platform.available ? 'Available now' : 'Coming soon'}
                      </Typography>
                    </Stack>
                    <Button
                      variant="contained"
                      startIcon={activeDownloadId === platform.id ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                      onClick={() => handleDownload(platform)}
                      disabled={!platform.available || activeDownloadId === platform.id}
                      sx={platform.available ? publicPrimaryButtonSx : publicSecondaryButtonSx}
                    >
                      {platform.available ? (activeDownloadId === platform.id ? 'Preparing...' : 'Download') : 'Coming soon'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </PublicPageFrame>
  );
}

export default DownloadsPage;
